import { getFormattedTime } from '../../../../shared/utils';
import { getLabelForEmailServiceProvider } from '../../../../shared/utils/get-email-accounts-esp-mapping';
import { mapSequenceStatusToContactFilter } from '../../../sequence/components/sequence-single-content/components/sequence-single-contacts/components/contact-list-content/components/helpers/step-filter-mapper';
import {
  CreationSourceDisplayName,
  ProspectCreationSource,
  SequenceProspectStatus,
} from '../../../sequence/enums';
import { EmailServiceProvider } from '../../../email-accounts/enums/email-account';
import { Outcome } from '../../../settings/types/outcome';

type Prospect = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  verificationStatus: string;
  finalStatus: SequenceProspectStatus;
  sequenceProspects?: any[];
  clientAssociated?: any[];
  tags?: { id: number; name: string }[];
  engagementFields?: {
    prospectId: number;
    numberOfEmailsSent?: string;
    numberOfOpens?: string;
    numberOfClicks?: string;
    recentlyOpenDate?: string;
    recentlyClickDate?: string;
    recentlyReplyDate?: string;
    lastContacted?: string;
  };
  attributes?: {
    id: number;
    fieldRefId: number;
    attributeValue: string | number;
  }[];
  user?: {
    id: number;
    firstName: string;
    lastName: string;
  };
  creationSource?: ProspectCreationSource;
  createdAt?: string;
  lastUpdatedAt?: string;
  isSubscribed?: boolean;
  isBounced?: boolean;
  verificationSubStatus?: string;
  mxRecord?: string;
  isFreeEmail?: boolean;
  emailServiceProvider?: EmailServiceProvider;
  attributesEnrichmentStatus?: { [key: string]: string };
};

const getProspectCreationSourceDisplayName = (
  creationSource: ProspectCreationSource,
) => {
  if (creationSource === ProspectCreationSource.manually_created) {
    return CreationSourceDisplayName.Manually_Created;
  }
  if (creationSource === ProspectCreationSource.csv_import) {
    return CreationSourceDisplayName.CSV_Import;
  }
  if (creationSource === ProspectCreationSource.lead_finder) {
    return CreationSourceDisplayName.Lead_Finder;
  }
  if (creationSource === ProspectCreationSource.chrome_extension) {
    return CreationSourceDisplayName.Chrome_Extention;
  }
  if (creationSource === ProspectCreationSource.api) {
    return CreationSourceDisplayName.API;
  }
  if (creationSource === ProspectCreationSource.unified_inbox) {
    return CreationSourceDisplayName.Unified_Inbox;
  }

  return '-';
};

export const getFormattedProspects = (
  prospects: Prospect[],
  outcomes: Outcome[],
) =>
  !prospects
    ? []
    : prospects.map(
        ({
          id,
          firstName,
          lastName,
          email,
          verificationStatus,
          sequenceProspects,
          tags: prospectTags,
          attributes,
          user,
          creationSource,
          createdAt,
          lastUpdatedAt,
          engagementFields,
          isSubscribed,
          isBounced,
          clientAssociated,
          emailServiceProvider,
          finalStatus,
          attributesEnrichmentStatus,
          ...rest
        }) => {
          let prospectStatus: SequenceProspectStatus;

          if (sequenceProspects?.length > 0) {
            const [{ isBlacklisted }] = sequenceProspects;
            prospectStatus = finalStatus;
            if (isBlacklisted) {
              prospectStatus = SequenceProspectStatus.BlackListed;
            }
          }

          if (isBounced) {
            prospectStatus = SequenceProspectStatus.Bounced;
          }

          if (!isSubscribed) {
            prospectStatus = SequenceProspectStatus.Unsubscribed;
          }

          const mappedAttributes = {};
          if (attributes && attributes.length > 0) {
            attributes.forEach((attr) => {
              mappedAttributes[attr.fieldRefId.toString()] =
                attr.attributeValue;
            });
          }

          let prospectOwner = '';
          if (user) {
            prospectOwner = `${user?.firstName || ''} ${
              user?.lastName || ''
            }`.trim();
          }

          let numberOfEmailsSent = '';
          let numberOfOpens = '';
          let numberOfClicks = '';
          let recentlyOpenDate = '';
          let recentlyClickDate = '';
          let recentlyReplyDate = '';
          let lastContacted = '';

          if (Object.keys(engagementFields || {}).length > 0) {
            numberOfEmailsSent = engagementFields?.numberOfEmailsSent || '';
            numberOfOpens = engagementFields?.numberOfOpens || '';
            numberOfClicks = engagementFields?.numberOfClicks || '';
            recentlyOpenDate = engagementFields?.recentlyOpenDate || '';
            recentlyClickDate = engagementFields?.recentlyClickDate || '';
            recentlyReplyDate = engagementFields?.recentlyReplyDate || '';
            lastContacted = engagementFields?.lastContacted || '';
          }

          let name = `${firstName || ''} ${lastName || ''}`.trim();

          if (name === '') {
            name = '(No Name)';
          }

          const inProgressTableCells = [];

          if (
            attributesEnrichmentStatus &&
            Object.keys(attributesEnrichmentStatus)?.length > 0
          ) {
            Object.entries(attributesEnrichmentStatus).forEach(
              ([key, value]) => {
                if (value === 'inProgress' || value === 'queued') {
                  inProgressTableCells.push(key);
                }
              },
            );
          }

          return {
            id,
            name,
            email,
            ...(verificationStatus && { verificationStatus }),
            ...(finalStatus && {
              finalStatus: mapSequenceStatusToContactFilter(finalStatus),
            }),
            ...(prospectStatus && {
              prospectStatus: mapSequenceStatusToContactFilter(finalStatus),
            }),
            ...(prospectOwner !== '' && { prospectOwner }),
            prospectTags: prospectTags || [],
            ...(sequenceProspects?.length > 0 && {
              sequenceProspects,
            }),
            ...(outcomes?.length > 0 && {
              outcomes,
            }),
            ...(Object.keys(mappedAttributes).length > 0 && mappedAttributes),
            ...(creationSource && {
              creationSource: getProspectCreationSourceDisplayName(
                creationSource,
              ),
            }),
            ...(createdAt && { creationDate: getFormattedTime(createdAt) }),
            ...(lastUpdatedAt && {
              lastUpdated: getFormattedTime(lastUpdatedAt),
            }),
            ...(numberOfOpens && { numberOfOpens }),
            ...(numberOfClicks && { numberOfClicks }),
            ...(numberOfEmailsSent && { numberOfEmailsSent }),
            ...(recentlyOpenDate && { recentlyOpenDate }),
            ...(recentlyClickDate && { recentlyClickDate }),
            ...(recentlyReplyDate && { recentlyReplyDate }),
            ...(lastContacted && { lastContacted }),
            ...(clientAssociated && { clientAssociated }),
            ...(emailServiceProvider && {
              emailServiceProvider: getLabelForEmailServiceProvider(
                emailServiceProvider,
              ),
            }),
            ...(inProgressTableCells?.length > 0 && {
              inProgressTableCells,
            }),
            ...rest,
          };
        },
      );
